import { Component, OnInit, ViewChild, Inject, AfterViewInit, OnDestroy, ElementRef } from '@angular/core';
import { Observable, BehaviorSubject, Subscription, of, Subject, merge } from 'rxjs';
import { concatMap, map, distinctUntilChanged, tap, catchError, filter } from 'rxjs/operators';
import { GridDataSource } from '../../../../shared/models/grid-data-source';
import * as moment from 'moment';
import { UserNotification, UserNotificationCommand } from '../../../../shared/components/notification-bar/notification-bar.model';
import { SettingsService } from '../../../../shared/services/settings/settings.service';
import * as _ from 'lodash';
import { CompanyBasicInfo } from '../../../../services/company.model';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { HttpErrorResponse } from '@angular/common/http';
import { UkgModal } from '@ignite/angular';
import { SortEventData } from '../../../pay-info/pay-info-table/ignite/web-components/sort-event';
import { PaginatorPageEvent, PaginatorPageEventRange } from '../../../pay-info/pay-info-table/ignite/web-components/paginator-page-event';
import { CompanyService } from '../../../../services/company.service';
import { PrenoteRequestService } from '../../../../services/prenote-request.service';
import { PrenoteHistoryFiltersService } from '../prenote-history-filters/prenote-history-filters.service';
import { PrenoteHistoryFilters } from '../prenote-history-filters/prenote-history-filters.model';
import { Router } from '@angular/router';
import { FileProcessingStatus } from '../../../../shared/enums/smarthub-enums';
import { PrenoteHistoryResult } from '../../../../services/prenote.model';
import { TranslateService } from '@ngx-translate/core';

class PrenoteHistoryDisplay {
  static translateStatus: (status: FileProcessingStatus) => string;
  companyNames: string[] = [];
  get companyNamesDisplay() {
    return this.companyNames
      .sort((a, b) => a.localeCompare(b))
      .join(', ');
  }
  get numCompanies() {
    return this.companyNames.length;
  }
  userDescription: string;
  datetimeOfRequest: Date;
  fileStatus: FileProcessingStatus;
  get status() {
    return PrenoteHistoryDisplay.translateStatus(this.fileStatus);
  }

  constructor(init?: Partial<PrenoteHistoryDisplay>) {
    Object.assign(this, init);
  }
}

@Component({
  selector: 'ssg-prenote-history-table',
  templateUrl: './prenote-history-table.component.html',
  styleUrls: ['./prenote-history-table.component.less']
})
export class PrenoteHistoryTableComponent implements OnInit, OnDestroy, AfterViewInit {

  dataSource: GridDataSource<PrenoteHistoryDisplay>;

  rawData: PrenoteHistoryDisplay[];
  private _rawDataTable: { [id: string]: PrenoteHistoryDisplay };

  private _manualFilterUpdate: Subject<any>;

  private _sortSubject: BehaviorSubject<SortEventData>;
  private _pageSubject: BehaviorSubject<PaginatorPageEventRange>;

  private _notification: UserNotification = null;

  allColumns: string[];
  showFilters = false;
  sortKey: SortEventData;

  records: PrenoteHistoryDisplay[];
  public initialRowsPerPage = 25 as number;
  public rowsOptions = [10, 25, 50, 100];
  private _dataSubscription: Subscription;

  @ViewChild('prenoteHistoryTableNotificationBar', { static: true, read: ElementRef }) notificationBarElement: ElementRef;

  get dataCount(): number {
    return (this.rawData || []).length;
  }

  readonly userMessages = {
    applyFilter: { message: 'COMPANIES.PRENOTE_HISTORY_APPLY_FILTER',
      type: 'user-info',
      severity: 'information',
      persistToStorage: true,
      distinct: true },
    noMatchingRecords: { message: 'COMPANIES.NO_MATCHING_RECORDS', type: 'status', severity: 'information' },
    success: { message: 'COMPANIES.SUCCESSFUL_QUERY', type: 'status', severity: 'success' },
  };

  notificationSubject: Subject<UserNotificationCommand> = new Subject<UserNotificationCommand>();

  private _setupStatusTranslation() {
    const translationByStatus = Object.keys(FileProcessingStatus).filter((item) => isNaN(Number(item))).reduce((mapped, val) => {
      const status = FileProcessingStatus[val] as FileProcessingStatus;
      const translationKeySuffix = status === FileProcessingStatus.NoFileNeeded
        ? 'NO_FILE_NEEDED'
        : val.toUpperCase();
        mapped[status] = this._translateService.instant(`COMPANIES.FILE_PROCESSING_STATUS_${translationKeySuffix}`);
      return mapped;
    }, {} as { [key: number]: string });
    PrenoteHistoryDisplay.translateStatus = (status) => translationByStatus[status] || String(status);
  }

  private _setStatusNotification(notif: string, params: any = null) {
    if (this._notification == null) {
      this._notification = new UserNotification(notif, params);
    }
  }

  private _publishNotification(notif: UserNotification): void {
    this.notificationSubject.next(new UserNotificationCommand('add', notif));
  }

  private _clearStatusNotification(): void {
    this.notificationSubject.next(new UserNotificationCommand('clearKey', this._notification));
    this._notification = null;
  }

  // Sorting Event in table
  handleSort(newKey: CustomEvent<SortEventData>): void {
    this._sortSubject.next((newKey as CustomEvent<SortEventData>).detail);
  }

  constructor(
    @Inject(TranslateService) private readonly _translateService: TranslateService,
    @Inject(PrenoteRequestService) private readonly _prenoteRequestService: PrenoteRequestService,
    @Inject(ScrollDispatcher) private readonly _scrollDispatcher: ScrollDispatcher,
    @Inject(PrenoteHistoryFiltersService) private readonly _filtersService: PrenoteHistoryFiltersService,
    private readonly router: Router,
    private readonly settings: SettingsService
  ) {
    this._setupStatusTranslation();
  }

  ngOnInit() {
    this.allColumns = [
      'companyNamesDisplay',
      'numCompanies',
      'userDescription',
      'datetimeOfRequest',
      'status'
    ];

    this._filtersService.clearFilters();
    this.records = [];
    this._rawDataTable = {};

    // (filters + API data -> this._setRawData)
    const apiDataPipeline = this._initApiDataPipeline();

    this._sortSubject = new BehaviorSubject<SortEventData>({ key: 'datetimeOfRequest', isAsc: false });
    const sortPipeline = this._sortSubject.pipe(
      distinctUntilChanged((previous: SortEventData, current: SortEventData) =>
        previous.key === current.key && previous.isAsc === current.isAsc));

    this._pageSubject = new BehaviorSubject<PaginatorPageEventRange>({ start: 0, end: this.initialRowsPerPage - 1 });
    const pagePipeline = this._pageSubject.pipe(
      distinctUntilChanged((previous: PaginatorPageEventRange, current: PaginatorPageEventRange) =>
        previous.start === current.start && previous.end === current.end));

    this.dataSource = new GridDataSource(
      apiDataPipeline,
      pagePipeline,
      sortPipeline);

    this._dataSubscription = this.dataSource.connect().subscribe(data => this.records = data);


  }

  ngOnDestroy() {
    this._dataSubscription.unsubscribe();
    this.records = [];
  }

  ngAfterViewInit() {
    this._publishNotification(new UserNotification('applyFilter', null));
  }

  private _initApiDataPipeline(): Observable<PrenoteHistoryDisplay[]> {

    this._manualFilterUpdate = new Subject<void>();
    let results: PrenoteHistoryDisplay[] = [];

    let lastFilters: PrenoteHistoryFilters = null;
    const sendManualFilters = function() {
      // Replicate a filter updated event any time we get an update to the
      // manualFilterUpdate subject, just copying the last real filters
      // (which may be null, but we filter out null filters in the first step below).
      return of(lastFilters);
    };
    const manualFiltersUpdate = this._manualFilterUpdate.pipe(sendManualFilters);

    const filtersSource = merge(this._filtersService.getFilters(), manualFiltersUpdate);
    return filtersSource.pipe(
      filter(filters => filters != null),
      tap(() => this._clearStatusNotification()),
      map((filters: PrenoteHistoryFilters) => {
        lastFilters = filters;
        return this._prenoteRequestService.getHistory(
          this.settings.tenantId,
          filters.companies.map(c => c.companyId)).pipe(
            catchError(error => {
              this._handleError(error);
              return of([] as PrenoteHistoryResult[]);
            })
          );
      }),
      concatMap(data => data),
      map(prenoteRequests => {
        results = [];

        for (const prenoteRequest of prenoteRequests) {
          const displayData = new PrenoteHistoryDisplay({
            companyNames: prenoteRequest.companies.map(c => c.companyName),
            userDescription: prenoteRequest.userDesc,
            datetimeOfRequest: prenoteRequest.fileProcessingUpdatedUtcDateTime,
            fileStatus: prenoteRequest.fileStatusValue
          });

          results.push(displayData);
        }

        return results;
      }),
      tap((prenoteRequests) => this._setRawData(prenoteRequests))
    );
  }

  private _setRawData(value: PrenoteHistoryDisplay[]) {
    this.rawData = value;
    if (this.dataCount === 0) {
      this._setStatusNotification('noMatchingRecords');
    }
    if (this._notification) {
      this._publishNotification(this._notification);
    }
    if (this.notificationBarElement && this.notificationBarElement.nativeElement) {
      const ancestorContainers = this._scrollDispatcher.getAncestorScrollContainers(this.notificationBarElement);
      ancestorContainers.forEach(s => s.scrollTo({ top: 0, left: 0 }));
    }
  }

  private _handleError(error: any) {
    this._setStatusNotification('internalServerError');
  }

  // Pagination event in Table
  public onPaginatorChanged(event: unknown) {
    this._pageSubject.next((event as CustomEvent<PaginatorPageEvent>).detail.range);
  }

  goToSendPrenoteFile() {
    this.router.navigate([{outlets: { eepay: 'viewcompanies'}}]);
  }
}
